import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
    loadOnboardingAgentDraft,
    saveOnboardingAgentDraft,
} from "../../OnboardingAgentPanel/helpers/onboardingAgentSessionStorage";

import { COMPOSER_TEXTAREA_AUTO_HEIGHT, MAX_TEXTAREA_HEIGHT_PX } from "../constants";

type UseOnboardingAgentComposerParams = {
    readonly onSend?: (message: string) => void;
    readonly onStop?: () => void;
    readonly isDisabled: boolean;
    readonly isLoading: boolean;
    readonly focusTrigger: number;
};

export const useOnboardingAgentComposer = ({
    onSend,
    onStop,
    isDisabled,
    isLoading,
    focusTrigger,
}: UseOnboardingAgentComposerParams) => {
    const [value, setValue] = useState<string>(loadOnboardingAgentDraft);
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        saveOnboardingAgentDraft(value);
    }, [value]);

    const trimmed = value.trim();
    const canSend = trimmed.length > 0 && !isDisabled && !isLoading;
    const isActivated = isFocused || canSend || isLoading;

    useLayoutEffect(() => {
        const el = textareaRef.current;
        if (!el) {
            return;
        }
        el.style.height = COMPOSER_TEXTAREA_AUTO_HEIGHT;
        el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
    }, [value]);

    useEffect(() => {
        if (isDisabled || isLoading) {
            return;
        }

        const el = textareaRef.current;
        if (!el) {
            return;
        }
        el.focus();
        const caretPosition = el.value.length;
        el.setSelectionRange(caretPosition, caretPosition);
    }, [focusTrigger, isDisabled, isLoading]);

    const handleSend = (): void => {
        if (!canSend) {
            return;
        }
        onSend?.(trimmed);
        setValue("");
    };

    const handleActionButtonClick = (): void => {
        if (isLoading) {
            onStop?.();
            return;
        }

        handleSend();
    };

    const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        setValue(event.target.value);
    };

    const handleFocus = (): void => {
        setIsFocused(true);
    };

    const handleBlur = (): void => {
        setIsFocused(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    return {
        value,
        textareaRef,
        canSend,
        isActivated,
        handleActionButtonClick,
        handleInputChange,
        handleKeyDown,
        handleFocus,
        handleBlur,
    };
};
